"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState, type ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/utils";
interface MousePosition {
  x: number;
  y: number;
}
function MousePosition() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      x: 0,
      y: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [mousePosition, setMousePosition] = useState(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleMouseMove = event => {
        setMousePosition({
          x: event.clientX,
          y: event.clientY
        });
      };
      window.addEventListener("mousemove", handleMouseMove);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return mousePosition;
}
interface ParticlesProps extends ComponentPropsWithoutRef<"div"> {
  className?: string;
  quantity?: number;
  staticity?: number;
  ease?: number;
  size?: number;
  refresh?: boolean;
  color?: string;
  vx?: number;
  vy?: number;
}
function hexToRgb(hex: string): number[] {
  hex = hex.replace("#", "");
  if (hex.length === 3) {
    hex = hex.split("").map(char => char + char).join("");
  }
  const hexInt = parseInt(hex, 16);
  const red = hexInt >> 16 & 255;
  const green = hexInt >> 8 & 255;
  const blue = hexInt & 255;
  return [red, green, blue];
}
type Circle = {
  x: number;
  y: number;
  translateX: number;
  translateY: number;
  size: number;
  alpha: number;
  targetAlpha: number;
  dx: number;
  dy: number;
  magnetism: number;
};
export const Particles: React.FC<ParticlesProps> = ({
  className = "",
  quantity = 100,
  staticity = 50,
  ease = 50,
  size = 0.4,
  refresh = false,
  color = "#ffffff",
  vx = 0,
  vy = 0,
  ...props
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const context = useRef<CanvasRenderingContext2D | null>(null);
  const circles = useRef<Circle[]>([]);
  const mousePosition = MousePosition();
  const mouse = useRef<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0
  });
  const canvasSize = useRef<{
    w: number;
    h: number;
  }>({
    w: 0,
    h: 0
  });
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio : 1;
  const rafID = useRef<number | null>(null);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);
  const initCanvasRef = useRef<() => void>(() => {});
  const onMouseMoveRef = useRef<() => void>(() => {});
  const animateRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (canvasRef.current) {
      context.current = canvasRef.current.getContext("2d");
    }
    initCanvasRef.current();
    animateRef.current();
    const handleResize = () => {
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
      resizeTimeout.current = setTimeout(() => {
        initCanvasRef.current();
      }, 200);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      if (rafID.current != null) {
        window.cancelAnimationFrame(rafID.current);
      }
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [color]);
  useEffect(() => {
    onMouseMoveRef.current();
  }, [mousePosition.x, mousePosition.y]);
  useEffect(() => {
    initCanvasRef.current();
  }, [refresh]);
  const initCanvas = () => {
    resizeCanvas();
    drawParticles();
  };
  const onMouseMove = () => {
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const {
        w,
        h
      } = canvasSize.current;
      const x = mousePosition.x - rect.left - w / 2;
      const y = mousePosition.y - rect.top - h / 2;
      const inside = x < w / 2 && x > -w / 2 && y < h / 2 && y > -h / 2;
      if (inside) {
        mouse.current.x = x;
        mouse.current.y = y;
      }
    }
  };
  const resizeCanvas = () => {
    if (canvasContainerRef.current && canvasRef.current && context.current) {
      canvasSize.current.w = canvasContainerRef.current.offsetWidth;
      canvasSize.current.h = canvasContainerRef.current.offsetHeight;
      canvasRef.current.width = canvasSize.current.w * dpr;
      canvasRef.current.height = canvasSize.current.h * dpr;
      canvasRef.current.style.width = `${canvasSize.current.w}px`;
      canvasRef.current.style.height = `${canvasSize.current.h}px`;
      context.current.scale(dpr, dpr);

      // Clear existing particles and create new ones with exact quantity
      circles.current = [];
      for (let i = 0; i < quantity; i++) {
        const circle = circleParams();
        drawCircle(circle);
      }
    }
  };
  const circleParams = (): Circle => {
    const x_0 = Math.floor(Math.random() * canvasSize.current.w);
    const y_0 = Math.floor(Math.random() * canvasSize.current.h);
    const translateX = 0;
    const translateY = 0;
    const pSize = Math.floor(Math.random() * 2) + size;
    const alpha = 0;
    const targetAlpha = parseFloat((Math.random() * 0.6 + 0.1).toFixed(1));
    const dx = (Math.random() - 0.5) * 0.1;
    const dy = (Math.random() - 0.5) * 0.1;
    const magnetism = 0.1 + Math.random() * 4;
    return {
      x: x_0,
      y: y_0,
      translateX,
      translateY,
      size: pSize,
      alpha,
      targetAlpha,
      dx,
      dy,
      magnetism
    };
  };
  const rgb = hexToRgb(color);
  const drawCircle = (circle_0: Circle, update = false) => {
    if (context.current) {
      const {
        x: x_1,
        y: y_1,
        translateX: translateX_0,
        translateY: translateY_0,
        size: size_0,
        alpha: alpha_0
      } = circle_0;
      context.current.translate(translateX_0, translateY_0);
      context.current.beginPath();
      context.current.arc(x_1, y_1, size_0, 0, 2 * Math.PI);
      context.current.fillStyle = `rgba(${rgb.join(", ")}, ${alpha_0})`;
      context.current.fill();
      context.current.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!update) {
        circles.current.push(circle_0);
      }
    }
  };
  const clearContext = () => {
    if (context.current) {
      context.current.clearRect(0, 0, canvasSize.current.w, canvasSize.current.h);
    }
  };
  const drawParticles = () => {
    clearContext();
    const particleCount = quantity;
    for (let i_0 = 0; i_0 < particleCount; i_0++) {
      const circle_1 = circleParams();
      drawCircle(circle_1);
    }
  };
  const remapValue = (value: number, start1: number, end1: number, start2: number, end2: number): number => {
    const remapped = (value - start1) * (end2 - start2) / (end1 - start1) + start2;
    return remapped > 0 ? remapped : 0;
  };
  const animate = () => {
    clearContext();
    circles.current.forEach((circle_2: Circle, i_1: number) => {
      // Handle the alpha value
      const edge = [circle_2.x + circle_2.translateX - circle_2.size,
      // distance from left edge
      canvasSize.current.w - circle_2.x - circle_2.translateX - circle_2.size,
      // distance from right edge
      circle_2.y + circle_2.translateY - circle_2.size,
      // distance from top edge
      canvasSize.current.h - circle_2.y - circle_2.translateY - circle_2.size // distance from bottom edge
      ];
      const closestEdge = edge.reduce((a, b) => Math.min(a, b));
      const remapClosestEdge = parseFloat(remapValue(closestEdge, 0, 20, 0, 1).toFixed(2));
      if (remapClosestEdge > 1) {
        circle_2.alpha += 0.02;
        if (circle_2.alpha > circle_2.targetAlpha) {
          circle_2.alpha = circle_2.targetAlpha;
        }
      } else {
        circle_2.alpha = circle_2.targetAlpha * remapClosestEdge;
      }
      circle_2.x += circle_2.dx + vx;
      circle_2.y += circle_2.dy + vy;
      circle_2.translateX += (mouse.current.x / (staticity / circle_2.magnetism) - circle_2.translateX) / ease;
      circle_2.translateY += (mouse.current.y / (staticity / circle_2.magnetism) - circle_2.translateY) / ease;
      drawCircle(circle_2, true);

      // circle gets out of the canvas
      if (circle_2.x < -circle_2.size || circle_2.x > canvasSize.current.w + circle_2.size || circle_2.y < -circle_2.size || circle_2.y > canvasSize.current.h + circle_2.size) {
        // remove the circle from the array
        circles.current.splice(i_1, 1);
        // create a new circle
        const newCircle = circleParams();
        drawCircle(newCircle);
      }
    });
    rafID.current = window.requestAnimationFrame(animateRef.current);
  };
  initCanvasRef.current = initCanvas;
  onMouseMoveRef.current = onMouseMove;
  animateRef.current = animate;
  return <div className={cn("pointer-events-none", className)} ref={canvasContainerRef} aria-hidden="true" {...props}>
      <canvas ref={canvasRef} className="size-full" />
    </div>;
};